import type { ReactNode } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { Input, Select, TextArea, Label } from '@/shared/components';
import type { TemplateMetadataFormValues } from '../schemas/documentTemplate.schema';
import { DOCUMENT_TYPE_OPTIONS } from '../utils/documentTypeLabels';

interface Props {
  control: Control<TemplateMetadataFormValues>;
  disabled?: boolean;
  /** Rendered between the name and the document type, e.g. the key on create. */
  afterName?: ReactNode;
}

const NAME_HINT = 'Shown in template lists and wherever a template is chosen.';
const DESCRIPTION_HINT = 'Help colleagues choose the right template. Up to 2000 characters.';

export function TemplateMetadataFields({ control, disabled = false, afterName }: Props) {
  return (
    <>
      <div className="sm:col-span-2">
        <Label htmlFor="template-name">Name</Label>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              id="template-name"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              error={!!error}
              hint={error?.message ?? NAME_HINT}
              hintId="template-name-hint"
              aria-invalid={!!error}
              aria-describedby="template-name-hint"
            />
          )}
        />
      </div>

      {afterName}

      <div className="sm:col-span-2">
        <Label htmlFor="template-document-type">Document type</Label>
        <Controller
          name="documentType"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              id="template-document-type"
              options={DOCUMENT_TYPE_OPTIONS}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              error={!!error}
              hint={error?.message}
              placeholder="Select a document type"
              ariaLabel="Document type"
            />
          )}
        />
      </div>


      <div className="sm:col-span-2">
        <Label htmlFor="template-description">Description</Label>
        <Controller
          name="description"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextArea
              id="template-description"
              rows={5}
              placeholder="Optional"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              error={!!error}
              hint={error?.message ?? DESCRIPTION_HINT}
            />
          )}
        />
      </div>
    </>
  );
}
